import { normalizeBrowserUrl } from '@hibro/shared';

/**
 * How the browser runtime presents a link no module claimed (见 20 第三档 / 22).
 *
 * The desktop has one answer — the built-in browser — because its
 * `WebContentsView` can host any page. This runtime has two, and the choice is
 * not a matter of taste: the in-app preview is a sandboxed `<iframe>`, which any
 * site may refuse with `X-Frame-Options` or CSP `frame-ancestors`, and nothing in
 * the page can reliably detect that refusal. So a real browser tab is the better
 * answer whenever it is available at all — and what decides that is whether the
 * click that started this still counts as user activation.
 */

/** Why a link ended up in the restricted in-app preview rather than a tab. */
export type InAppPreviewReason =
  /** The click's transient activation expired before the request came back. */
  | 'no-user-activation'
  /** Activation was live, but the browser refused the popup anyway. */
  | 'popup-blocked'
  /** Not an `http(s)` address, so opening it at all is not this code's call. */
  | 'unsupported-scheme';

export type UnownedLinkPresentation =
  | { readonly where: 'new-tab' }
  | { readonly where: 'in-app-preview'; readonly reason: InAppPreviewReason };

/** What {@link presentUnownedLink} needs from the page it runs in. */
export interface UnownedLinkRuntime {
  /**
   * Whether a popup-gated open is still worth attempting — see
   * {@link hasLiveUserActivation}.
   */
  hasUserActivation(): boolean;
  /** Opens a new browser tab, resolving whether the browser allowed it. */
  openNewTab(url: string): boolean;
}

/**
 * Whether the window still holds transient user activation, which is what
 * `window.open` consumes.
 *
 * Transient activation is **window state with a timeout**, not something bound to
 * the event callback's own frame, so it survives the SharedWorker round trip a
 * clicked link makes on this runtime (见 18) as long as that round trip is quick.
 * A slow owning module (`APP_LINK_HANDLER_TIMEOUT_MS`) or a cold module bundle
 * fetch can outlast it, which is precisely the case this predicate catches.
 *
 * Unknown counts as live: on a browser without the `UserActivation` API (it has
 * been Baseline since November 2023, so this is a narrow tail) attempting is
 * strictly better than assuming failure, because the attempt is self-verifying —
 * a blocked `window.open` returns `null` and lands on the same in-app preview,
 * with the same log.
 */
export function hasLiveUserActivation(): boolean {
  return navigator.userActivation?.isActive ?? true;
}

/**
 * Present `url`, preferring a real browser tab over the restricted in-app
 * preview.
 *
 * Not a fallback chain that hides a failure: both outcomes are real
 * presentations of the address, and the reason travels with the answer so
 * "why did this open in a tab last time and not now" stays answerable from the
 * log.
 *
 * Non-`http(s)` addresses deliberately do **not** get a tab. `mailto:` and
 * `tel:` reach here whenever no module claims them, and handing an arbitrary
 * scheme to the OS is an open security decision (见 20 已知缺口) — not something
 * to settle as a side effect of this branch. They keep today's behaviour: the
 * in-app preview refuses them through `normalizeBrowserUrl` and says so.
 */
export function presentUnownedLink(
  url: string,
  runtime: UnownedLinkRuntime,
): UnownedLinkPresentation {
  let target: string;
  try {
    target = normalizeBrowserUrl(url);
  } catch {
    return { where: 'in-app-preview', reason: 'unsupported-scheme' };
  }
  if (!runtime.hasUserActivation()) {
    return { where: 'in-app-preview', reason: 'no-user-activation' };
  }
  return runtime.openNewTab(target)
    ? { where: 'new-tab' }
    : { where: 'in-app-preview', reason: 'popup-blocked' };
}
